import { DetailsPageTopControlsView } from "../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { NotSet } from "../../common/misc/UIconstants";
import Detail from "../../common/panels/Detail/Detail";
import { Table } from "../../common/panels/Detail/DetailTable";
import { EntityDetailProps, EntityDetailTableProps } from "../../common/entity/EntityInterfaces";
import { toUppercase } from "../../common/helperfunctions/stringFunctions";
import {
  CustomImport,
  customImportConstants,
  CustomImportLog,
  customImportLogConstants,
  CustomImportResult,
} from "../../api/CustomImports";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { Dataset, datasetsConstants } from "../../api/Datasets";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import { useContext } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import { Link, useHistory } from "react-router-dom";
import { getDetailLink, getIndexRoute, ResourceName } from "../../main/Routing";
import { useEntityDetail, usePostMutation } from "../../api/BaseEntityApi";
import { LoadingWrapper } from "../../common/LoadingWrapper";
import { useEntityPermissions } from "../../common/permissions/useEntityPermissions";
import { Button } from "../../common/buttons/Button/Button";
import { showtoast } from "../../common/overlays/Toasts/showtoast";
import { useQueryClient } from "@tanstack/react-query";
import { EntityDeleteButton } from "../../Entities/GenericComponents/common/GenericEntityDetailPage";

export const CustomImportLogsDetailTable = ({
  entity,
  entityConstants,
  fieldLabels,
  showLinks = true,
}: EntityDetailTableProps<"customImportLogs">) => {
  const { session, route } = useContext(SessionContext);

  const {
    data: dataset,
    fetchStatus: fetchStatusDataset,
    status: statusDataset,
  } = useEntityDetail<Dataset>(datasetsConstants.resource, entity.datasetId, undefined, {
    enabled: !!entity.datasetId,
  });

  const {
    data: customImport,
    fetchStatus: fetchStatusCustomImport,
    status: statusCustomImport,
  } = useEntityDetail<CustomImport>(customImportConstants.resource, entity.customImportId, undefined, {
    enabled: !!entity.datasetId,
  });

  return (
    <>
      <Table noPadding nonFoldable>
        <Table.Head>Details</Table.Head>
        <Table.Body>
          <Table.Body.RowContent
            title={fieldLabels.timestamp}
            content={<DateTimeRenderer date={entity.timestamp} includeDate includeTime includeElapsed />}
          />

          <Table.Body.RowContent
            title={fieldLabels.type}
            content={CustomImportLogsTypeRenderer({ type: entity.type })}
          />

          <Table.Body.RowContent
            title={fieldLabels.message}
            content={
              <div className="container_label">
                <div className="container_label_name">{entity.message ?? NotSet}</div>
              </div>
            }
          />

          <Table.Body.RowContent
            title={fieldLabels.datasetId}
            content={
              <LoadingWrapper status={statusDataset} fetchStatus={fetchStatusDataset}>
                {showLinks ? (
                  <div className="container_label">
                    <div className="container_label_name">
                      <Link
                        style={{ width: "100%", textDecoration: "none" }}
                        to={route(getDetailLink(datasetsConstants.frontendIndexRoute, entity.datasetId))}
                      >
                        <LucideIcon name={datasetsConstants.icon} color="var(--primary)" />{" "}
                        {dataset?.name ?? entity.datasetId ?? NotSet}
                      </Link>
                    </div>
                    {!!dataset?.name && (
                      <div className={"container_label_id"}>
                        <span style={{ whiteSpace: "nowrap" }}>
                          {toUppercase(datasetsConstants.entitySingular)}-ID: {entity.datasetId}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="container_label">
                    <div className="container_label_name">
                      <LucideIcon name={datasetsConstants.icon} color="var(--primary)" />{" "}
                      {dataset?.name ?? entity.datasetId ?? NotSet}
                    </div>
                    {!!dataset?.name && (
                      <div className={"container_label_id"}>
                        <span style={{ whiteSpace: "nowrap" }}>
                          {toUppercase(datasetsConstants.entitySingular)}-ID: {entity.datasetId}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </LoadingWrapper>
            }
          />

          <Table.Body.RowContent
            title={fieldLabels.customImportId}
            content={
              <LoadingWrapper status={statusCustomImport} fetchStatus={fetchStatusCustomImport}>
                {showLinks ? (
                  <div className="container_label">
                    <div className="container_label_name">
                      <Link
                        style={{ width: "100%", textDecoration: "none" }}
                        to={route(getDetailLink(customImportConstants.frontendIndexRoute, entity.customImportId))}
                      >
                        <LucideIcon name={customImportConstants.icon} color="var(--primary)" />{" "}
                        {customImport?.name ?? entity.customImportId ?? NotSet}
                      </Link>
                    </div>
                    {!!dataset?.name && (
                      <div className={"container_label_id"}>
                        <span style={{ whiteSpace: "nowrap" }}>
                          {toUppercase(customImportConstants.entitySingular)}-ID: {entity.customImportId}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="container_label">
                    <div className="container_label_name">
                      <LucideIcon name={customImportConstants.icon} color="var(--primary)" />{" "}
                      {dataset?.name ?? entity.customImportId ?? NotSet}
                    </div>
                    {!!dataset?.name && (
                      <div className={"container_label_id"}>
                        <span style={{ whiteSpace: "nowrap" }}>
                          {toUppercase(customImportConstants.entitySingular)}-ID: {entity.customImportId}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </LoadingWrapper>
            }
          />

          {(session?.permissions.view_admin_pages || session?.permissions.logsadmin) && (
            <Table.Body.RowContent
              title={fieldLabels.payload}
              content={
                <pre style={{ width: "100%", padding: "10px", margin: 0 }}>
                  {JSON.stringify(entity.payload, null, 2)}
                </pre>
              }
            />
          )}
        </Table.Body>
      </Table>
    </>
  );
};

export const CustomImportLogsDetail = ({
  permissions,
  routes,
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  entityTypeId,
}: EntityDetailProps<"customImportLogs">) => {
  const queryClient = useQueryClient();

  const { canReadIndex } = useEntityPermissions({ entityTypeId: "customImports" });

  const { replace } = useHistory();

  const { route } = useContext(SessionContext);

  const { mutateAsync: bulkApplyCustomImportMutationAsync, isLoading: isLoadingBulkApplyCustomImportMutation } =
    usePostMutation<CustomImportResult[], { datasetIds: number[] }>({
      resource: "custom_imports/datasets" as ResourceName,
    });

  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          entity={entity}
          entityConstants={customImportLogConstants}
          hideHistoryModal
          nameOverride={`ID: ${entity.id} - ${entity.type}`}
          controls={
            <div className="flex align-center gap-5">
              <CustomImportLogsErrorDownloadButton customImportLog={entity} />
              <Button
                className="btn btn-soft-success"
                title={`Apply ${customImportConstants.entitySingular} to ${entity.datasetId}`}
                onClick={async () => {
                  await bulkApplyCustomImportMutationAsync(
                    { body: { datasetIds: [entity.datasetId] } },
                    {
                      onSuccess: (data) => {
                        if (data.every((d) => !d.error)) {
                          showtoast(
                            "success",
                            `Successfully applied ${customImportConstants.entitySingular} to dataset with ID ${entity.datasetId}`
                          );
                          replace(route(getIndexRoute(customImportLogConstants.frontendIndexRoute)));
                        } else {
                          showtoast(
                            "error",
                            `Applying ${customImportConstants.entitySingular} to dataset with ID ${entity.datasetId} failed`
                          );
                          console.error(
                            `Applying ${customImportConstants.entitySingular} to dataset with ID ${entity.datasetId} failed`,
                            data
                          );
                          queryClient.invalidateQueries({
                            queryKey: [customImportLogConstants.resource],
                          });
                        }
                      },
                    }
                  ).catch(() => {});
                }}
                loading={isLoadingBulkApplyCustomImportMutation}
                disabled={!canReadIndex}
              >
                <LucideIcon name={customImportConstants.icon} /> Apply {customImportConstants.entitySingular}
              </Button>
              <EntityDeleteButton
                entityTypeId="customImportLogs"
                entity={entity as any}
                alwaysDeletePermanently
                btnClassName={(e) => "btn btn-soft-danger"}
              />
            </div>
          }
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <CustomImportLogsDetailTable
          entity={entity}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels}
          entityTypeId={entityTypeId}
        />
      }
      sidebarPosition="hidden"
      // sidebar={<></>}
    />
  );
};

export const CustomImportLogsTypeRenderer = ({ type }: { type: CustomImportLog["type"] }) => {
  switch (type) {
    case "Error":
      return (
        <div className="container_label">
          <div className="container_label_name">
            <div className="label label-soft-danger">{type}</div>
          </div>
        </div>
      );
    case "Warning":
      return (
        <div className="container_label">
          <div className="container_label_name">
            <div className="label label-soft-warning">{type}</div>
          </div>
        </div>
      );
    case "Info":
      return (
        <div className="container_label">
          <div className="container_label_name">
            <div className="label label-soft-info">{type}</div>
          </div>
        </div>
      );
    default:
      return <></>;
  }
};

const CustomImportLogsErrorDownloadButton = ({ customImportLog }: { customImportLog: CustomImportLog }) => {
  const handleDownload = () => {
    var jsonString =
      "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(customImportLog.payload, null, 2));
    const link = document.createElement("a");
    link.href = jsonString;
    link.download = `error_report_${customImportLog.customImportId}_${customImportLog.datasetId}_${new Date(
      customImportLog.timestamp
    ).toISOString()}.json`;
    link.click();
  };

  return (
    <Button className="btn btn-default flex align-center gap-5" onClick={() => handleDownload()}>
      <LucideIcon name="download" />
      Download error report
    </Button>
  );
};
